import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Title } from '@tremor/react'
import { CheckIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid'
import ProjectProgressBar from './ProjectProgressBar'
import ProjectSubTabs from './ProjectSubTabs'
import CircularProgress from './CircularProgress'

function MobileProgressWithSubtabs({ currentStepIndex, setCurrentStepIndex, tabs, currentTab, onTabClick, getSubtabsForStep, completedSteps = [], completedSubsteps = [] }) {
    const { t } = useTranslation()

    const allSteps = [
        { id: '01', name: t('Projects.ProgressSteps.Prepare'), description: t('Projects.ProgressSteps.PrepareDescription') },
        { id: '02', name: t('Projects.ProgressSteps.Question'), description: t('Projects.ProgressSteps.QuestionDescription') },
        { id: '03', name: t('Projects.ProgressSteps.Analyze'), description: t('Projects.ProgressSteps.AnalyzeDescription') },
        { id: '04', name: t('Projects.ProgressSteps.Report'), description: t('Projects.ProgressSteps.ReportDescription') },
    ]

    // Generate steps with dynamic status based on completion tracking
    const steps = allSteps.map((step, index) => ({
        ...step,
        href: '#',
        status: completedSteps.includes(index) ? 'complete' : index === currentStepIndex ? 'current' : 'upcoming',
        completed: completedSteps.includes(index)
    }))

    const handleStepClick = (stepIndex) => {
        setCurrentStepIndex(stepIndex)
    }

    return (
        <div className="space-y-3">
            {steps.map((step, stepIdx) => (
                <div key={step.name} className="space-y-2">
                    {/* Main Step */}
                    <div className="border border-gray-300 rounded-md bg-white">
                        <button 
                            onClick={() => handleStepClick(stepIdx)} 
                            className="w-full flex items-center px-6 py-4 text-sm font-medium text-left"
                        >
                            <span className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
                                step.status === 'complete' 
                                    ? 'bg-iavisus-100' 
                                    : step.status === 'current'
                                    ? 'bg-iavisus-900'
                                    : 'border-2 border-gray-300'
                            }`}>
                                {step.status === 'complete' ? (
                                    <CheckIcon className="size-6 text-white" />
                                ) : step.status === 'current' ? (
                                    <span className="text-white">{step.id}</span>
                                ) : (
                                    <span className="text-gray-500">{step.id}</span>
                                )}
                            </span>
                            <div className="ml-4 flex flex-col text-left">
                                <span className={`text-base font-bold ${
                                    step.status === 'complete' 
                                        ? 'text-iavisus-100' 
                                        : step.status === 'current'
                                        ? 'text-iavisus-100'
                                        : 'text-gray-500'
                                }`}>{step.name}</span>
                                <span className={`text-sm ${
                                    step.status === 'complete' 
                                        ? 'text-gray-500' 
                                        : step.status === 'current'
                                        ? 'text-iavisus-200'
                                        : 'text-gray-500'
                                }`}>{step.description}</span>
                            </div>
                        </button>
                    </div>

                    {/* Show subtabs for current step */}
                    {stepIdx === currentStepIndex && (
                        <div className="ml-6 border border-gray-300 rounded-md bg-white">
                            <div className="divide-y divide-gray-300">
                                {getSubtabsForStep(stepIdx).map((tab) => {
                                    const isCompleted = completedSubsteps.includes(tab.displayId);
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => onTabClick(tab.id)}
                                            className="w-full flex items-center pl-3 pr-4 py-2 text-sm font-medium whitespace-nowrap"
                                        >
                                            <span className={`flex size-6 shrink-0 items-center justify-center rounded-full ${
                                                isCompleted 
                                                    ? 'bg-iavisus-100' 
                                                    : tab.current 
                                                    ? 'bg-iavisus-900' 
                                                    : 'border-2 border-gray-300'
                                            }`}>
                                                {isCompleted ? (
                                                    <CheckIcon className="size-4 text-white" />
                                                ) : (
                                                    <span className={`text-xs ${
                                                        tab.current 
                                                            ? 'text-white' 
                                                            : 'text-gray-500'
                                                    }`}>{tab.displayId}</span>
                                                )}
                                            </span>
                                            <span className={`ml-2 text-xs font-bold ${
                                                isCompleted 
                                                    ? 'text-iavisus-100'
                                                    : tab.current 
                                                    ? 'text-iavisus-100' 
                                                    : 'text-gray-500'
                                            }`}>{tab.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

function ProjectFlowCard({ tabs, currentTab, onTabClick, currentStep, setCurrentStepIndex, completedSteps = [], completedSubsteps = [] }) {
    const { t } = useTranslation()
    const [isCollapsed, setIsCollapsed] = useState(false)

    // Get subtabs for a specific step
    const getSubtabsForStep = (stepIndex) => {
        let stepTabs = []
        switch (stepIndex) {
            case 0: // Prepare - research planning tabs, bibliography and behavioral economics
                stepTabs = [11, 12, 15, 13, 14, 9, 18, 17]  // + Tendências, Economia Comportamental
                break;
            case 1: // Question - subjects, videoconferences, questionnaires
                stepTabs = [6, 10, 16]  // Subjects, VideoConferences, Questionnaires
                break;
            case 2: // Analyze - analysis first, then everything else except report tabs
                const otherTabs = tabs.filter(tab => ![9, 6, 10, 11, 12, 15, 13, 14, 16, 17, 18, 7, 8].includes(tab.id)).map(tab => tab.id)
                stepTabs = [0, ...otherTabs]  // Analysis first, then other tabs
                break;
            case 3: // Report - results and articles
                stepTabs = [7, 8]  // CategoryResults, Articles
                break;
        }
        
        return tabs.filter(tab => stepTabs.includes(tab.id)).map((tab, index) => ({
            ...tab,
            displayId: `${stepIndex + 1}.${index + 1}`
        }))
    }

    // Calculate progress based on completed steps
    const calculateProgress = () => {
        const totalSteps = 4; // Prepare, Question, Analyze, Report
        const completedStepCount = completedSteps.length;
        return (completedStepCount / totalSteps) * 100;
    };

    const progress = calculateProgress();

    return (
        <div data-testid="project-flow-card" className='bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl mx-8 mt-8 mb-8'>
            <Card className={`px-6 pt-6 ${isCollapsed ? 'pb-6' : 'pb-6'}`}>
                {/* Title with Toggle */}
                <div className={`flex items-center justify-between ${isCollapsed ? 'mb-0' : 'mb-6'}`}>
                    <div className="text-2xl font-bold text-iavisus-100">
                        {t('Projects.ProjectFlow')}
                    </div>
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label={isCollapsed ? t('Projects.ShowFlow') : t('Projects.HideFlow')}
                    >
                        {isCollapsed ? (
                            <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                        ) : (
                            <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                        )}
                    </button>
                </div>

                {/* Progress Content - Collapsible */}
                {!isCollapsed && (
                    <>
                        {/* Mobile Layout - Stack Vertically with Integrated Subtabs */}
                        <div className="block md:hidden">
                            {/* Circular Progress Bar - Mobile First */}
                            <div className="flex justify-center mb-6">
                                <CircularProgress progress={progress} />
                            </div>

                            {/* Mobile Custom Progress with Integrated Subtabs */}
                            <div className="space-y-3">
                                {/* Mobile version of progress steps with subtabs integrated */}
                                <MobileProgressWithSubtabs 
                                    currentStepIndex={currentStep} 
                                    setCurrentStepIndex={setCurrentStepIndex}
                                    tabs={tabs}
                                    currentTab={currentTab}
                                    onTabClick={onTabClick}
                                    getSubtabsForStep={getSubtabsForStep}
                                    completedSteps={completedSteps}
                                    completedSubsteps={completedSubsteps}
                                />
                            </div>
                        </div>

                        {/* Desktop Layout - Side by Side */}
                        <div className="hidden md:flex md:items-center md:gap-6">
                            {/* Circular Progress Bar */}
                            <div className="flex-shrink-0">
                                <CircularProgress progress={progress} />
                            </div>

                            {/* Progress Steps and Subtabs */}
                            <div className="flex-grow min-w-0">
                                {/* Main Progress Bar */}
                                <div className="mb-3">
                                    <ProjectProgressBar
                                        currentStepIndex={currentStep}
                                        setCurrentStepIndex={setCurrentStepIndex}
                                        completedSteps={completedSteps}
                                    />
                                </div>

                                {/* Sub Tabs */}
                                <ProjectSubTabs
                                    tabs={getSubtabsForStep(currentStep)}
                                    currentTab={currentTab}
                                    onTabClick={onTabClick}
                                    currentStep={currentStep}
                                    completedSubsteps={completedSubsteps}
                                />
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}

export default ProjectFlowCard
